import { useEffect, useMemo, useState } from "react";
import { useCart } from "../lib/CartContext";
import { createCheckoutSession, getMyCredits } from "../lib/api";

function formatDollars(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

export default function CartPage() {
  const { items, removeItem, clearEvent } = useCart();
  // Credits are per club, so each event can only use the balance at the
  // club running it.
  const [spendableByEvent, setSpendableByEvent] = useState<Record<string, number>>({});
  const [useCredits, setUseCredits] = useState(false);
  const [creditsToApplyDollars, setCreditsToApplyDollars] = useState("0");
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [checkingOutEventId, setCheckingOutEventId] = useState<string | null>(null);

  const eventIdsKey = [...new Set(items.map((i) => i.eventId))].sort().join(",");
  useEffect(() => {
    for (const eventId of eventIdsKey ? eventIdsKey.split(",") : []) {
      getMyCredits(eventId)
        .then((res) => setSpendableByEvent((prev) => ({ ...prev, [eventId]: res.spendableCents ?? 0 })))
        .catch(() => {});
    }
  }, [eventIdsKey]);
  const anySpendable = Object.values(spendableByEvent).some((c) => c > 0);

  const groups = useMemo(() => {
    const byEvent = new Map<string, { eventTitle: string; items: { item: typeof items[number]; index: number }[] }>();
    items.forEach((item, index) => {
      const g = byEvent.get(item.eventId) ?? { eventTitle: item.eventTitle, items: [] };
      g.items.push({ item, index });
      byEvent.set(item.eventId, g);
    });
    return Array.from(byEvent.entries()).map(([eventId, g]) => ({ eventId, ...g }));
  }, [items]);

  const grandTotalCents = items.reduce((sum, i) => sum + i.entryFeeCents, 0);

  async function handleCheckout(eventId: string, eventTotalCents: number) {
    setError(null);
    setInfo(null);
    setCheckingOutEventId(eventId);
    try {
      let creditsAppliedCents = 0;
      const spendable = spendableByEvent[eventId] ?? 0;
      if (useCredits && spendable) {
        const requestedCents = Math.round(parseFloat(creditsToApplyDollars || "0") * 100);
        creditsAppliedCents = Math.max(0, Math.min(requestedCents, spendable, eventTotalCents));
      }
      const racers = items.filter((i) => i.eventId === eventId).map((i) => ({ racerName: i.racerName, racerClass: i.racerClass }));
      const res = await createCheckoutSession({ eventId, racers, creditsAppliedCents });
      if (res.freeCheckout) {
        setInfo("Registration complete — no payment was required.");
        clearEvent(eventId);
      } else if (res.checkoutUrl) {
        window.location.assign(res.checkoutUrl);
      }
    } catch (err) {
      // If Stripe is unavailable the backend keeps the registrations and
      // holds any credits ("Payment provider unavailable, your credits are
      // held; ..."). The cart stays so checking out again retries; the
      // retry reuses the held registrations rather than taking credits twice.
      setError(err instanceof Error ? err.message : "Checkout failed.");
    } finally {
      setCheckingOutEventId(null);
    }
  }

  if (items.length === 0) {
    return (
      <div>
        <h1>Cart</h1>
        <p className="muted">Your cart is empty. Add racers from an event's registration section.</p>
      </div>
    );
  }

  return (
    <div>
      <h1>Cart</h1>
      {error && <p className="err">{error}</p>}
      {info && <p className="ok">{info}</p>}

      <p className="notice">
        Items from different events are checked out separately — each event below has its own
        checkout button and its own Stripe session.
      </p>

      {anySpendable && (
        <div className="card">
          <label>
            <input
              type="checkbox"
              style={{ width: "auto", marginRight: "0.5rem" }}
              checked={useCredits}
              onChange={(e) => setUseCredits(e.target.checked)}
            />
            Use available credits
          </label>
          {useCredits && (
            <div style={{ marginTop: "0.5rem" }}>
              <label htmlFor="credits-amount">Amount to apply ($)</label>
              <input
                id="credits-amount"
                type="number"
                min={0}
                step="0.01"
                value={creditsToApplyDollars}
                onChange={(e) => setCreditsToApplyDollars(e.target.value)}
              />
              <p className="muted" style={{ fontSize: 12 }}>
                Applied per event at checkout, clamped to your credits at that event's club and the
                event's total. Credits from one club can't be used at another.
              </p>
            </div>
          )}
        </div>
      )}

      {groups.map((g) => {
        const eventTotalCents = g.items.reduce((sum, { item }) => sum + item.entryFeeCents, 0);
        return (
          <div className="card cart-group" key={g.eventId}>
            <h3>{g.eventTitle}</h3>
            {(spendableByEvent[g.eventId] ?? 0) > 0 && (
              <p className="muted" style={{ fontSize: 13 }}>
                Credits available at this club: {formatDollars(spendableByEvent[g.eventId])}
              </p>
            )}
            {g.items.map(({ item, index }) => (
              <div className="cart-line" key={index}>
                <span>
                  {item.racerName} — {item.racerClass}
                </span>
                <span>
                  {formatDollars(item.entryFeeCents)}{" "}
                  <button className="link" onClick={() => removeItem(index)}>
                    remove
                  </button>
                </span>
              </div>
            ))}
            <div className="cart-total">
              <span>Event total</span>
              <span>{formatDollars(eventTotalCents)}</span>
            </div>
            <button
              style={{ marginTop: "0.75rem" }}
              disabled={checkingOutEventId === g.eventId}
              onClick={() => handleCheckout(g.eventId, eventTotalCents)}
            >
              {checkingOutEventId === g.eventId ? "Starting checkout…" : `Checkout for ${g.eventTitle}`}
            </button>
          </div>
        );
      })}

      <div className="cart-total" style={{ fontSize: "1.1rem" }}>
        <span>Grand total (all events)</span>
        <span>{formatDollars(grandTotalCents)}</span>
      </div>
    </div>
  );
}
